import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import AdvancedSearch from 'components/search-bar/advanced-search';
import Button, { PRIMARY_THEME, SECONDARY_THEME, TERTIARY_THEME } from 'components/control/button';
import LocationSearch from 'components/location-search';
import FilterIcon from 'components/icon/filter-icon';
import MapIcon from 'components/icon/map-icon';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import MapListToggleButtonOnMap, { MAP_VIEW, ViewType } from './map_list_toggle_button';
import BuyOrRentFilterDropdown from './buy_or_rent_filter_dropdown';
import PropertyTypeFilterDropdown from './home_type_filter_dropdown';
import BedBathFilterDropdown, { Bedrooms, Bathrooms } from './bed_bath_filter_dropdown';
import { Result, useExperiment } from '@growthbook/growthbook-react';
import { trackEvent } from 'utils/google-tag-manager';
import { getSearchPredictionSortOptions, sortSearchPredictions } from 'components/suggested-location-dropdown';
import styles from './style.module.scss';
import { searchFilterIds } from 'constants/test-constants';
import { useIsMobile } from 'hooks/use-size-class';
import { ThemeNames } from 'types/themes';
import { useFeaturesContext, useThemeContext } from 'contexts';
import { getTenantById } from '@tenants';
import Cookies from 'js-cookie';
import { MLML_AGENT_COOKIE_NAME } from 'constants/cookies';
import MyLinkMyLeadCreate from 'components/my-link-my-lead-create';
import { propertyTypeOptions } from 'utils/select-options';
import { legacyPropertyTypeOptions } from 'utils/select-options';
import { BEDS_FILTER_OPTION, BUY_FILTER_OPTION, EXACT_NUMBER_FILTER_OPTION, PRICE_FILTER_OPTION, RENT_FILTER_OPTION, SQFT_FILTER_OPTION } from 'constants/area-map-page-filters';
import { getNumberOfPropertyTypesFiltered } from 'utils/listing-query-helper';
import { getNumberOfLegacyPropertyTypesFiltered } from 'utils/listing-query-helper';
import { LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import dynamic from 'next/dynamic';
import {
  NOT_AVAILABLE_OTHER_STATUS,
  NOT_AVAILABLE_SOLD_STATUS,
  PENDING_STATUS,
  type Filter,
  type PropertyTypeFilter,
  type Status,
} from 'contexts/preferences/listing-params';

import type { PartialDeep } from 'type-fest';
import type SearchPrediction from 'data/search-predictions';
import { buildClassName } from 'utils/build-class-name';
import { FilterChipBar } from './filter_chip_bar';
import RangeFilterDropdown from './range_filter_dropdown';


const NoImageListingsFilterWarning = dynamic(() => import('components/no-image-listings-filter-warning'), { ssr: false });

//#region Types

export type ListingFilterBarType = Pick<Filter, 'slug' | 'areaName' | 'rental' | 'status' | 'homeType' | 'priceMin' | 'priceMax' | 'bedrooms' | 'bathrooms' | 'sqftMin' | 'sqftMax' | 'garage' | 'parkingSpaces' | 'openHouse' | 'pool' | 'waterfront' | 'fireplace' | 'hasImage' | 'additional' | 'listedSince' | 'listedTo'> & {
  city?: string;
  province?: string;
};

// Define specific filter types
export type PriceRangeFilter = { type: 'priceRange'; min: number | null; max: number | null };
export type BedroomsFilter = { type: 'bedrooms'; bedrooms: string };
export type BathroomsFilter = { type: 'bathrooms'; bathrooms: string };
export type PropertyTypeFilterItem = { type: 'propertyType'; homeType: PropertyTypeFilter };
export type StatusFilter = { type: 'status'; status: Status };
export type RentalFilter = { type: 'rental'; rental: boolean };
export type SqftFilter = { type: 'sqft'; sqftMin: number | null; sqftMax: number | null };
// Individual filter types for each feature filter (individually removable)
export type GarageFilter = { type: 'garage'; garage: boolean };
export type PoolFilter = { type: 'pool'; pool: boolean };
export type WaterfrontFilter = { type: 'waterfront'; waterfront: boolean };
export type FireplaceFilter = { type: 'fireplace'; fireplace: boolean };
export type OpenHouseFilter = { type: 'openHouse'; openHouse: boolean };
export type ParkingSpacesFilter = { type: 'parkingSpaces'; parkingSpaces: string };
export type HasImageFilter = { type: 'hasImage'; hasImage: boolean };
export type LockerFilters = { type: 'locker'; locker: string }; 
export type MaintenanceFeeFilters = { type: 'maintenanceFee'; maintenanceFee: number | null };
export type ListedSinceFilter = { type: 'listedSince'; listedSince: string };
export type ListedToFilter = { type: 'listedTo'; listedTo: string };
export type CityFilter = { type: 'city'; city: string };
export type ProvinceFilter = { type: 'province'; province: string };
export type CityAndProvinceFilter = { type: 'cityAndProvince'; city: string; province: string };
// Union of all possible filter items
export type FilterItemData =
  | PriceRangeFilter
  | BedroomsFilter
  | BathroomsFilter
  | PropertyTypeFilterItem
  | StatusFilter
  | RentalFilter
  | SqftFilter
  | GarageFilter
  | PoolFilter
  | WaterfrontFilter
  | FireplaceFilter
  | OpenHouseFilter
  | ParkingSpacesFilter
  | HasImageFilter
  | LockerFilters
  | MaintenanceFeeFilters
  | ListedSinceFilter
  | ListedToFilter
  | CityFilter
  | ProvinceFilter
  | CityAndProvinceFilter;

// Function to derive active filters from filter props
export const getActiveFilters = (filterProps: ListingFilterBarType): FilterItemData[] => {
  const activeFilters: FilterItemData[] = [];
  const selectedTypes = filterProps.homeType ? Object.keys(filterProps.homeType).filter(key => filterProps.homeType[key]) : [];

  if ((filterProps.priceMin !== undefined && filterProps.priceMin !== null) ||
    (filterProps.priceMax !== undefined && filterProps.priceMax !== null)) {
    activeFilters.push({
      type: 'priceRange',
      min: filterProps.priceMin || null,
      max: filterProps.priceMax || null,
    });
  }

  if ((filterProps.sqftMin !== undefined && filterProps.sqftMin !== null) ||
    (filterProps.sqftMax !== undefined && filterProps.sqftMax !== null)) {
    activeFilters.push({
      type: 'sqft',
      sqftMin: filterProps.sqftMin || null,
      sqftMax: filterProps.sqftMax || null,
    });
  }

  if (filterProps.bedrooms !== undefined &&
    filterProps.bedrooms !== null &&
    filterProps.bedrooms !== 'null' &&
    filterProps.bedrooms !== '0+') {
    activeFilters.push({
      type: 'bedrooms',
      bedrooms: filterProps.bedrooms,
    });
  }

  if (filterProps.bathrooms !== undefined &&
    filterProps.bathrooms !== null &&
    filterProps.bathrooms !== 'null' &&
    filterProps.bathrooms !== '0+') {
    activeFilters.push({
      type: 'bathrooms',
      bathrooms: filterProps.bathrooms,
    });
  }

  if (Object.values(filterProps.homeType).some(Boolean) && selectedTypes.length !== 6) {
    activeFilters.push({
      type: 'propertyType',
      homeType: filterProps.homeType,
    });
  }

  // Add city and province as display-only filters
  if (filterProps.city && filterProps.province) {
    activeFilters.push({
      type: 'cityAndProvince',
      city: filterProps.city,
      province: filterProps.province,
    });
  } else if (filterProps.city) {
    activeFilters.push({
      type: 'city',
      city: filterProps.city,
    });
  } else if (filterProps.province) {
    activeFilters.push({
      type: 'province',
      province: filterProps.province,
    });
  }

  if (filterProps.status === NOT_AVAILABLE_SOLD_STATUS || filterProps.status === NOT_AVAILABLE_OTHER_STATUS) {
    activeFilters.push({
      type: 'status',
      status: filterProps.status,
    });
  }
  if (filterProps.status === PENDING_STATUS) {
    activeFilters.push({
      type: 'status',
      status: filterProps.status,
    });
  }

  if (filterProps.rental === true) {
    activeFilters.push({
      type: 'rental',
      rental: filterProps.rental,
    });
  }

  // Individual misc filters - each gets its own chip for individual removal
  if (filterProps.garage === true) {
    activeFilters.push({
      type: 'garage',
      garage: filterProps.garage,
    });
  }
  
  if (filterProps.pool === true) {
    activeFilters.push({
      type: 'pool',
      pool: filterProps.pool,
    });
  }
  
  if (filterProps.waterfront === true) {
    activeFilters.push({
      type: 'waterfront',
      waterfront: filterProps.waterfront,
    });
  }
  
  if (filterProps.fireplace === true) {
    activeFilters.push({
      type: 'fireplace',
      fireplace: filterProps.fireplace,
    });
  }
  
  if (filterProps.openHouse === true) {
    activeFilters.push({
      type: 'openHouse',
      openHouse: filterProps.openHouse,
    });
  }

  if (filterProps.parkingSpaces !== '0+') {
    activeFilters.push({
      type: 'parkingSpaces',
      parkingSpaces: filterProps.parkingSpaces,
    });
  }

  if (filterProps.hasImage !== true) {
    activeFilters.push({
      type: 'hasImage',
      hasImage: filterProps.hasImage,
    });
  }

  if (filterProps.additional?.condoOrTownhouse?.locker && 
      filterProps.additional.condoOrTownhouse.locker !== 'any') {
    activeFilters.push({
      type: 'locker',
      locker: filterProps.additional.condoOrTownhouse.locker,
    });
  }

  if (filterProps.additional?.condoOrTownhouse?.maintenanceFee) {
    activeFilters.push({
      type: 'maintenanceFee',
      maintenanceFee: filterProps.additional.condoOrTownhouse.maintenanceFee,
    });
  }

  if (filterProps.listedSince) {
    activeFilters.push({
      type: 'listedSince',
      listedSince: filterProps.listedSince,
    });
  } 

  if (filterProps.listedTo) {
    activeFilters.push({
      type: 'listedTo',
      listedTo: filterProps.listedTo,
    });
  }

  return activeFilters;
};

export type ListingFilterBarProps = {
  filters: ListingFilterBarType;
  mapListToggleViewType?: ViewType;
  showNoImageListingsFilterWarning?: boolean;
  onValueChange: (filters: PartialDeep<ListingFilterBarType>, changedFilter?: string) => void;
  onSaveSearchButtonClick?: () => void;
  activeFilterItems: FilterItemData[];
  setActiveFilterItems: (activeFilterItems: FilterItemData[]) => void;
  onLocationSearchClick: (searchPrediction: SearchPrediction) => void;
  onMapIconClick?: () => void;
  onMoreButtonClick: () => void;
  onMapListToggleButtonClick?: () => void;
  onNoImageListingsFilterWarningClose?: () => void;
  hideMapViewButton?: boolean;
  hideFilterChipBarSaveSearchButton?: boolean;
  /** When true, show Create Link for My Link My Lead in the filter bar (aligned with search options) even when not on /search. Use on area/hybrid pages so the button stays with search options. */
  showMLMLCreateInFilterBar?: boolean;
};
//#endregion

//#region Constants
const MobileAdvancedSearchClassName = styles['mobile-search'];
const FiltersContainerClassName = styles['filters-container'];
const FiltersLeftContainerClassName = styles['filters-left-container'];
const FiltersRightContainerClassName = styles['filters-right-container'];
const SearchContainerClassName = styles['search-container'];
const PaddedButtonClassName = styles['padded-button'];
const SaveSearchButtonClassName = styles['save-search'];
const MiddleFiltersContainerClassName = styles['middle-filters'];
const MLMLCreateClassName = styles['mlml-create-custom'];
const SqftFilterClassName = styles['sqft-filter'];
//#endregion


export const SavedSearchButton = ({ onSaveSearchButtonClick, abTestingFiltersButton }: { onSaveSearchButtonClick: () => void; abTestingFiltersButton?: Result<number> }) => {
  const { t } = useTranslation(Namespaces.SEARCH);
  const { themeName } = useThemeContext();
  const isExpTheme = themeName && themeName !== ThemeNames.ZOOCASA;

  const saveSearchButtonStyle = abTestingFiltersButton ? abTestingFiltersButton.variationId === 1 && isExpTheme ? SECONDARY_THEME : TERTIARY_THEME : TERTIARY_THEME;

  return (
    <div className={SaveSearchButtonClassName}>
      <Button
        theme={saveSearchButtonStyle}
        label={t(LOCALE_SEARCH_PATHS.SAVED_SEARCH_BUTTON_SAVE, {
          defaultValue: 'Save Search',
        })}
        onClick={onSaveSearchButtonClick}
        testId={searchFilterIds.saveSearchButtonMapPage}
      />
    </div>
  );
};

export const ListingFilterBar = ({ 
  filters, 
  mapListToggleViewType = MAP_VIEW,
  onSaveSearchButtonClick,
  activeFilterItems,
  setActiveFilterItems,
  onMapIconClick, 
  onLocationSearchClick, 
  onValueChange, 
  onMoreButtonClick, 
  onMapListToggleButtonClick, 
  showNoImageListingsFilterWarning, 
  onNoImageListingsFilterWarningClose,
  hideMapViewButton,
  hideFilterChipBarSaveSearchButton,
  showMLMLCreateInFilterBar = false,
}: ListingFilterBarProps) => {
  const { t } = useTranslation(Namespaces.SEARCH);
  const router = useRouter();
  const isOnMap = router.route === '/search';
  const isMobile = useIsMobile();
  const { features } = useFeaturesContext();
  const [isLocationDropdownActive, setIsLocationDropdownActive] = useState(false);
  const [locationQuery, setLocationQuery] = useState('');
  const [searchBarPlaceholder, setSearchBarPlaceholder] = useState<string>();
  const abTestingFiltersButton = useExperiment({ key: 'more-filters', variations: [0, 1]});
  const { tenantId } = useThemeContext();
  const tenant = getTenantById(tenantId);
  const showsMLMLCreate = tenant?.features.MyLinkMyLead && !!Cookies.get(MLML_AGENT_COOKIE_NAME);
  const showMLMLInLeftBar = (isOnMap || showMLMLCreateInFilterBar) && showsMLMLCreate;
  const isExpanded = activeFilterItems.length > 0;

  const { slug, areaName, rental, homeType, priceMin, priceMax, bedrooms, bathrooms, sqftMin, sqftMax } = filters;

  const handleBuyOrRentFilterChange = useCallback((value: boolean) => {
    const isDefaultBuyOrRentValue = rental === false && value === false;
    if (!isDefaultBuyOrRentValue) {
      onValueChange({
        rental: value,
        priceMin: null,
        priceMax: null,
      }, value ? RENT_FILTER_OPTION : BUY_FILTER_OPTION);
    }
  }, [onValueChange, rental]);

  const handlePropertyTypeFilterChange = useCallback((value: PropertyTypeFilter) => {
    if (value !== homeType) {
      const toggledPropertyType = Object.keys(value).find(
        propertyType => value[propertyType] !== homeType[propertyType]
      );
      onValueChange({ homeType: value }, toggledPropertyType);
    }
  }, [onValueChange, homeType]);

  const handleBedroomsFilterChange = useCallback((value: string, checkedUseExact?: boolean) => {
    const isDefaultBedroomsValue = bedrooms === '0+' && value === '0+';
    if (value !== bedrooms && !isDefaultBedroomsValue) {
      onValueChange({ bedrooms: value }, checkedUseExact ? EXACT_NUMBER_FILTER_OPTION : BEDS_FILTER_OPTION);
    }
  }, [onValueChange, bedrooms]);

  const handleBathroomsFilterChange = useCallback((value: string, checkedUseExact?: boolean) => {
    const isDefaultBathroomsValue = bathrooms === undefined && value === '0+';
    if (value !== bathrooms && !isDefaultBathroomsValue) {
      onValueChange({ bathrooms: value }, checkedUseExact ? EXACT_NUMBER_FILTER_OPTION : BEDS_FILTER_OPTION);
    }
  }, [onValueChange, bathrooms]);

  const handlePriceRangeFilterChange = useCallback((min: number | null, max: number | null) => {
    if ((min != priceMin) || (max != priceMax)) {
      if (min && max && min > max) {
        onValueChange({ priceMin: min || null, priceMax: min || null }, PRICE_FILTER_OPTION);
      } else {
        onValueChange({ priceMin: min || null, priceMax: max || null }, PRICE_FILTER_OPTION);
      }
    }
  }, [onValueChange, priceMin, priceMax]);

  const handleSqftRangeFilterChange = useCallback((min: number | null, max: number | null) => {
    if ((min != sqftMin) || (max != sqftMax)) {
      onValueChange({ sqftMin: min || null, sqftMax: max || null }, SQFT_FILTER_OPTION);
    }
  }, [onValueChange, sqftMin, sqftMax]);

  const handleLocationSearchClick = useCallback((searchPrediction: SearchPrediction) => {
    setSearchBarPlaceholder(searchPrediction.description.split(',')[0]);
    onLocationSearchClick(searchPrediction);
    setIsLocationDropdownActive(false);
    setLocationQuery(searchPrediction.description);
  }, [onLocationSearchClick]);

  const handleSearchIconClick = useCallback((searchPredictions?: SearchPrediction[]) => {
    if (locationQuery.length >= 3 && searchPredictions && searchPredictions.length > 0) {
      const sortOptions = getSearchPredictionSortOptions(locationQuery, searchPredictions);
      const suggestedResults = sortSearchPredictions(searchPredictions, sortOptions);
      const prioritizedGroupResults = sortOptions.prioritizeListings
        ? [...suggestedResults.listings, ...suggestedResults.locations]
        : [...suggestedResults.locations, ...suggestedResults.listings];
      const flattenedResults = [
        ...suggestedResults.militaryBases,
        ...prioritizedGroupResults,
        ...suggestedResults.schools,
      ];
      handleLocationSearchClick(flattenedResults[0]);
    }
  }, [handleLocationSearchClick, locationQuery.length]);

  useEffect(() => {
    if (areaName) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocationQuery(areaName);
      setSearchBarPlaceholder(areaName.split(',')[0]);
    }
  }, [areaName]);

  useEffect(() => {
    trackEvent(abTestingFiltersButton.variationId === 1 ? 'XpB_QFBut' : 'XpA_QFBut');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update activeFilterItems when filters prop changes
  useEffect(() => {
    setActiveFilterItems(getActiveFilters(filters));
  }, [filters]);

  return (
    <>
      <div className={buildClassName(styles.component, isExpanded && styles.expanded)}>
        {isMobile && <div className={MobileAdvancedSearchClassName}>
          <AdvancedSearch onValueChange={onValueChange} onFilterButtonClick={onMoreButtonClick} />
        </div>}
        <div className={FiltersContainerClassName}>
          <div className={FiltersLeftContainerClassName}>
            <div className={SearchContainerClassName} ref={useOutsideClickHandler(() => setIsLocationDropdownActive(false))}>
              <LocationSearch
                locationQuery={locationQuery}
                setLocationQuery={setLocationQuery}
                isLocationDropdownActive={isLocationDropdownActive}
                setIsLocationDropdownActive={setIsLocationDropdownActive}
                onClick={handleLocationSearchClick}
                placeholder={searchBarPlaceholder}
                onSearchIconClick={handleSearchIconClick}
              />
            </div>
            <BuyOrRentFilterDropdown
              rental={rental}
              onValueChange={handleBuyOrRentFilterChange}
            />
            <div className={MiddleFiltersContainerClassName}>
              <PropertyTypeFilterDropdown
                propertyType={homeType}
                propertyTypeFilterLabel={`${t(LOCALE_SEARCH_PATHS.FILTER_LABELS_HOME_TYPE, { defaultValue: 'Home Type' })} (${features.useLegacySearchFilter ? getNumberOfLegacyPropertyTypesFiltered(homeType) : getNumberOfPropertyTypesFiltered(homeType)})`}
                onValueChange={handlePropertyTypeFilterChange}
                propertyTypeList={features.useLegacySearchFilter ? legacyPropertyTypeOptions : propertyTypeOptions}
              />
              <RangeFilterDropdown
                min={priceMin}
                max={priceMax}
                rental={rental}
                rangeType="price"
                onValueChange={handlePriceRangeFilterChange}
              />
              <BedBathFilterDropdown
                bedrooms={bedrooms as Bedrooms}
                bathrooms={bathrooms as Bathrooms}
                onBedroomsFilterChange={handleBedroomsFilterChange}
                onBathroomsFilterChange={handleBathroomsFilterChange}
              />
              <div className={SqftFilterClassName}>
                <RangeFilterDropdown
                  min={sqftMin}
                  max={sqftMax}
                  rangeType="sqft"
                  onValueChange={handleSqftRangeFilterChange}
                />
              </div>
            </div>
            <span className={PaddedButtonClassName}>
              <div className={styles['more-filters-button-wrapper']}>
                <Button
                  Icon={FilterIcon}
                  theme={PRIMARY_THEME}
                  label={t(LOCALE_SEARCH_PATHS.FILTER_LABELS_MORE_FILTERS, { defaultValue: 'More Filters' })}
                  onClick={onMoreButtonClick}
                  testId={searchFilterIds.moreButtonMapPage}
                />
                <NoImageListingsFilterWarning onClose={onNoImageListingsFilterWarningClose} showNoImageListingsFilterWarning={showNoImageListingsFilterWarning} />
              </div>
            </span>
            {isOnMap && !showsMLMLCreate &&
              <SavedSearchButton 
                onSaveSearchButtonClick={onSaveSearchButtonClick} 
                abTestingFiltersButton={abTestingFiltersButton} />
            }
            {showMLMLInLeftBar && <MyLinkMyLeadCreate className={MLMLCreateClassName} />}
          </div>
          <div className={FiltersRightContainerClassName}>
            {(isOnMap && onMapListToggleButtonClick) &&
              <MapListToggleButtonOnMap
                slug={slug}
                viewType={mapListToggleViewType}
                onClick={onMapListToggleButtonClick}
                isMobile={isMobile}
              />
            }
            {!isOnMap && !hideMapViewButton &&
              <Button
                Icon={MapIcon}
                theme={SECONDARY_THEME}
                label={t(LOCALE_SEARCH_PATHS.FILTER_VIEW_MAP_VIEW, {
                  defaultValue: 'Map View',
                })}
                onClick={onMapIconClick}
                testId={searchFilterIds.mapButtonAreaPage}
              />
            }
            {hideMapViewButton &&
              <SavedSearchButton 
                onSaveSearchButtonClick={onSaveSearchButtonClick} 
                abTestingFiltersButton={abTestingFiltersButton} />
            }
          </div>
        </div>
      </div>
      {(activeFilterItems.length > 0 && !isOnMap) && 
          <FilterChipBar
            activeFilterItems={activeFilterItems}
            setActiveFilterItems={setActiveFilterItems}
            filters={filters}
            onValueChange={onValueChange}
            onSaveSearchButtonClick={onSaveSearchButtonClick}
            isMobile={isMobile}
            hideSaveSearchButton={hideFilterChipBarSaveSearchButton}
          />
      }
    </>
  );
};